import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { Contact } from '../../types/contact.type';
import { Observable, of } from 'rxjs';
import { tap, catchError, switchMap, shareReplay } from 'rxjs/operators';
import { CompanyContact } from '../../types/company-contact.type';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@src/app/store/auth/auth.selectors';
import { take } from 'rxjs/operators';

export interface CreateContactPayload {
  nombre: string;
  apellido1: string;
  apellido2?: string;
  email: string;
  telefono?: string;
  telefono_movil?: string;
  cargo?: string;
  funciones: number[];
  privacy: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContactsService extends ApiService<Contact[]> {
  private contactsCache: Map<string, CompanyContact[]> = new Map();
  private inFlightRequests: Map<string, Observable<CompanyContact[]>> =
    new Map();
  private lastCompanyId: string | null = null;

  constructor(private store: Store) {
    super();
    this.moduleUrl.set('/headless');
  }

  clearCache(): void {
    this.contactsCache.clear();
    this.inFlightRequests.clear();
  }

  getCompanyContacts(
    clearCache: boolean = false,
  ): Observable<CompanyContact[]> {
    return this.store.select(AuthSelectors.selectSelectedCompany).pipe(
      take(1),
      switchMap((company) => {
        // Validate company exists
        if (!company || !company.CLIEN_NM) {
          console.warn(
            '⚠️ ContactsService: No valid company selected, returning empty array',
          );
          return of([]);
        }

        const companyId = company.CLIEN_NM;

        // Clear cache if company changed
        if (
          clearCache ||
          (this.lastCompanyId && this.lastCompanyId !== companyId)
        ) {
          this.clearCache();
        }
        this.lastCompanyId = companyId;
        // Check cache first
        if (this.contactsCache.has(companyId)) {
          return of(this.contactsCache.get(companyId)!);
        }

        // Check if request is already in flight
        if (this.inFlightRequests.has(companyId)) {
          return this.inFlightRequests.get(companyId)!;
        }

        // Create new request
        const request$ = this.http
          .get<CompanyContact[]>(`${this.url()}/getContactosEmpresa`)
          .pipe(
            tap((contacts) => {
              this.contactsCache.set(companyId, contacts);
              this.inFlightRequests.delete(companyId);
            }),
            catchError((error) => {
              console.error(
                '❌ ContactsService: API error for company:',
                companyId,
                error,
              );
              this.inFlightRequests.delete(companyId);
              throw error;
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
          );

        // Store in-flight request
        this.inFlightRequests.set(companyId, request$);
        return request$;
      }),
    );
  }

  createContact(payload: CreateContactPayload): Observable<any> {
    return this.http.post<any>(`${this.url()}/contactos/crear`, payload);
  }

  updateContactProfiles(
    contactId: string,
    profileIds: string[],
  ): Observable<{ success: boolean; message?: string; [key: string]: any }> {
    return this.http.patch<{
      success: boolean;
      message?: string;
      [key: string]: any;
    }>(`${this.url()}/contactos/${contactId}/perfiles`, {
      funciones: profileIds.map((id) => parseInt(id, 10)),
    });
  }

  updateContact(
    contactId: string,
    payload: {
      nombre: string;
      apellido1: string;
      apellido2?: string;
      email: string;
      telefono?: string;
      telefono_movil?: string;
      cargo?: string;
    },
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.url()}/contactos/${contactId}`,
      payload,
    );
  }

  removeContact(contactId: string): Observable<any> {
    return this.http.post<any>(`${this.url()}/contactos/${contactId}/baja`, {});
  }
}
